import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Breadcrumb, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import qs from 'query-string';

import generatePath from '~settings/routing';
import {
  queryHeroBanner,
  deletedHeroBanner,
  createHeroBanner,
  updateHeroBanner,
  updateBannerPositions,
} from '../gql';

import BannerList from './components/banner-list';
import AddBannerModal from './components/add-banner-modal';
import LoadingModal from './components/loading-modal';

export default function HeroBanner({ match, location }) {
  const { t } = useTranslation();
  const cityName = match?.params?.name;
  const { slug: citySlug, cityId, locale } = qs.parse(location?.search);
  const [visible, setVisible] = useState(false);
  const [loadingModalVisible, setLoadingModalVisible] = useState(false);
  const [bannerList, setBannerList] = useState([]);
  const [currentBannerId, setCurrentBannerId] = useState();

  const [queryBannerList, { loading: bannerLoading }] = useMutation(queryHeroBanner, {
    fetchPolicy: 'no-cache',
    variables: { cityId, locale },
    onCompleted: data => {
      const result = data.listBanners?.banner ?? [];
      setBannerList(result);
    },
  });

  const [createBanner, { loading: createBannerLoading }] = useMutation(createHeroBanner, {
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      const result = data.createBanner?.banner?.id ?? [];
      if (result) {
        setLoadingModalVisible(false);
        setVisible(false);
        queryBannerList();
      }
    },
    onError: () => {
      setLoadingModalVisible(false);
    },
  });

  const [updateBanner, { loading: updateBannerLoading }] = useMutation(updateHeroBanner, {
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      const result = data.updateBanner?.banner?.id ?? [];
      if (result) {
        setLoadingModalVisible(false);
        setVisible(false);
        queryBannerList();
      }
    },
    onError: () => {
      setLoadingModalVisible(false);
    },
  });

  const [deleteBanner] = useMutation(deletedHeroBanner, {
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      const result = data.deleteBanner.success;
      if (result) {
        setLoadingModalVisible(false);
        queryBannerList();
      }
    },
    onError: () => {
      setLoadingModalVisible(false);
    },
  });

  const [adjustBannerPositions] = useMutation(updateBannerPositions, {
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      const result = data.updateBannerPositions.success;
      if (result) {
        queryBannerList();
      }
    },
  });

  const handleCreateBanner = variables => {
    setLoadingModalVisible(true);
    createBanner(variables);
  };

  const handleUpdateBanner = variables => {
    setLoadingModalVisible(true);
    updateBanner(variables);
  };

  const handleDeleteBanner = variables => {
    setLoadingModalVisible(true);
    deleteBanner(variables);
  };

  useEffect(() => {
    queryBannerList();
  }, []);

  return (
    <div className="hero-banner-container">
      <div className="breadcrumb-wrapper">
        <Breadcrumb>
          <Breadcrumb.Item className="link-font">
            <Link to={generatePath('strategy')}>
              {t('cms.dashboard.strategy.city_homepage.subtitle')}
            </Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item className="link-font">
            <Link
              to={generatePath(
                'strategy.cityHomepage',
                { name: cityName },
                { slug: citySlug, cityId },
              )}
            >
              {cityName}
            </Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item className="link-font-active">Hero banner setting</Breadcrumb.Item>
        </Breadcrumb>
        <div className="setting-line">
          <p className="setting-title">
            Hero banner setting
            <span className="subtitle">(The maximum banner list limits: 30)</span>
          </p>
          <If condition={bannerList.length > 0 && bannerList.length < 30}>
            <Button
              className="setting-button"
              icon="plus"
              onClick={() => {
                setVisible(true);
                setCurrentBannerId(null);
              }}
            >
              Add new banner
            </Button>
          </If>
        </div>
      </div>
      <BannerList
        t={t}
        list={bannerList}
        deleteBanner={handleDeleteBanner}
        cityName={cityName}
        loading={bannerLoading}
        setCurrentBannerId={setCurrentBannerId}
        addBanner={() => setVisible(true)}
        adjustBannerPositions={adjustBannerPositions}
      />
      <If condition={visible}>
        <AddBannerModal
          t={t}
          onClose={() => setVisible(false)}
          visible={visible}
          citySlug={citySlug}
          locale={locale}
          loading={createBannerLoading}
          updateLoading={updateBannerLoading}
          createBanner={handleCreateBanner}
          updateHeroBanner={handleUpdateBanner}
          bannerId={currentBannerId}
        />
      </If>
      <If condition={loadingModalVisible}>
        <LoadingModal visible={loadingModalVisible} />
      </If>
    </div>
  );
}
